import { Component, Input, OnChanges, OnDestroy, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ValuesService } from '@app/common/values/values.service';
import { CUF_PARTNER_LEGAL_FORM } from '@app/shared/constants';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import * as common from '@app/shared/_models/common';
import { pickedDateStr } from '@app/shared/time';
import { SmartSelectComponent } from '@app/shared/smart-select/smart-select.component';

@Component({
    selector: 'app-cuf',
    imports: [FormsModule, ReactiveFormsModule, MatCheckboxModule, MatRadioModule, MatIconModule, MatDatepickerModule, SmartSelectComponent],
    templateUrl: './cuf.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './cuf.component.scss',
})
export class CufComponent implements OnChanges, OnDestroy {
	private valuesService = inject(ValuesService);

	@Input() cufGroup!: FormGroup;
	@Input() cufsStructure: common.Cuf[] = [];
	/** true = label + input sur la même ligne, false = label au-dessus */
	@Input() inline: boolean = true;  // boolean qui détermine la présentation du label sur la même ligne que l'input ou au dessus
	/** 'edit' = valeur unique (saisit la valeur du cuf), 'selector' = plage ≥/≤ pour les numériques (filtre produit...) */
	@Input() mode: 'edit' | 'selector' = 'edit';

	// ============================================================
	// LOV — deux façons de nommer une liste, et une seule est cachée
	// ============================================================
	// `data.values` : la liste EN DUR dans la définition du cuf (tableau de chaînes). La valeur
	// enregistrée est la chaîne elle-même. C'est la forme d'origine, inchangée.
	//
	// `data.values_id` : la liste EMPRUNTÉE à un référentiel c_values (« Forme juridique » vise
	// le groupe 27). La valeur enregistrée est alors l'ID de l'élément, jamais son libellé —
	// renommer « SARL » ne doit pas délier les fiches qui la portent, et c'est déjà la
	// convention de typ, typpay et roles.
	//
	// DEUX LISTES PRÉPARÉES PAR RÉFÉRENTIEL, et c'est voulu : `open` sans les formes fermées
	// aux nouvelles inscriptions (data.closed_to_new — l'EIRL depuis 2022), `all` avec. Une
	// fiche qui PORTE déjà une forme fermée reçoit la liste complète, sinon son select
	// paraîtrait vide alors que la valeur est là et parfaitement légitime. Les deux tableaux
	// sont des références STABLES : `getCufLovItems` est appelé à chaque cycle de détection,
	// un `filter` y referait un tableau neuf à chaque fois et le select se reconstruirait sans
	// cesse.
	private lovOpen = new Map<number, { label: string; value: any }[]>();
	private lovAll = new Map<number, { label: string; value: any }[]>();
	private lovSubs: Subscription[] = [];
	/** Les champs PILOTES d'une règle visible_when écoutés : un dépendant se revalide quand son
	 *  pilote change (voir applyRules). Recâblés à chaque nouveau groupe ou structure. */
	private ruleSubs: Subscription[] = [];
	/** Le groupe sur lequel CE composant a posé des règles : c'est lui qu'il nettoie en partant. */
	private ruledGroup: FormGroup | null = null;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['cufsStructure']) this.loadLovs();
		// Les règles de la définition (mandatory, min, max, entier) sont posées SUR LES CONTROLS
		// dès que le composant a les deux : la structure et le groupe. En mode « selector »
		// (filtres de la liste produits), les controls sont des bornes g/l, pas des valeurs :
		// aucune règle ne s'y applique.
		if (this.mode === 'edit' && (changes['cufsStructure'] || changes['cufGroup'])) {
			const group = this.cufGroup ?? null;
			const structure = this.cufsStructure;
			this.ruledGroup = group;
			this.watchPilots();
			// HORS du cycle de détection en cours : poser (ou retirer, ngOnDestroy) une règle change
			// la validité du formulaire, que l'écran hôte a déjà lue plus haut dans son gabarit
			// (« Enregistrer » grisé sur formGroup.invalid). En synchrone, Angular le signale
			// (NG0100) et le bouton reste un cycle en retard ; en microtâche, le cycle suivant
			// part avec la bonne validité. Le groupe a pu changer entre-temps : on ne règle que lui.
			queueMicrotask(() => { if (this.ruledGroup === group) CufComponent.applyRules(group, structure); });
		}
	}

	/** Un dépendant (visible_when) ne se revalide pas tout seul quand son pilote change : Angular
	 *  remonte la validité vers les parents, jamais vers les frères. On l'y pousse. */
	private watchPilots(): void {
		this.ruleSubs.forEach((s) => s.unsubscribe());
		this.ruleSubs = [];
		if (!this.cufGroup) return;
		for (const cuf of this.cufsStructure ?? []) {
			const regle = cuf.data?.visible_when;
			if (!regle || regle.cuf === undefined || regle.cuf === null || cuf.id === undefined || cuf.id === null) continue;
			const pilot = this.cufGroup.get('' + regle.cuf);
			const dependent = this.cufGroup.get('' + cuf.id);
			if (!pilot || !dependent) continue;
			this.ruleSubs.push(pilot.valueChanges.subscribe(() => dependent.updateValueAndValidity({ emitEvent: false })));
		}
	}

	// ============================================================
	// LES RÈGLES DE LA DÉFINITION — data.mandatory, data.min, data.max, data.unit, data.hint
	// ============================================================
	// CE QUI N'EXISTAIT PAS AVANT (2026-09-15) : `mandatory` ne dessinait qu'un astérisque, et
	// aucune borne n'existait — une taille pouvait se saisir en mètres ici, en centimètres là,
	// et 78 706 adresses avaient un « Niveau * » vide, enregistrées sans un mot. Désormais la
	// définition du cuf PRESCRIT, et c'est ce composant, et lui seul, qui l'applique — tous les
	// écrans qui le rendent en héritent, sans une ligne chez eux :
	//   "mandatory": true          -> Validators.required ; l'astérisque dit enfin vrai
	//   "min": 60, "max": 300      -> Validators.min / max (INT et FLOAT), et les bornes en clair
	//                                 dans l'infobulle
	//   typ INT                    -> valeur entière exigée (validator) ET saisie filtrée
	//                                 (chiffres seuls, comme le type FK)
	//   "unit": "cm"               -> suffixe gris collé au champ (« 175 cm »), repris dans
	//                                 l'infobulle : l'unité se voit AVANT de saisir
	//   "hint": "…"                -> infobulle ; à défaut, composée du nom, de l'unité et des
	//                                 bornes (« Taille — en cm, entre 60 et 300 »)
	//   "pattern": "^[0-9]{14}$"   -> Validators.pattern, le motif que le serveur refuse aussi à l'écriture
	//                                 (partner::model::cufs_refuses) ; en faute, le champ dit data.hint
	// Un control en faute porte un filet rouge et son infobulle dit la règle enfreinte ; c'est
	// l'écran hôte qui, sur formGroup.invalid, refuse d'enregistrer (fiche tiers : bouton grisé).
	// Les règles sont REMPLACÉES, pas empilées : rejouer applyRules sur un groupe déjà réglé
	// (rechargement de la fiche, nouvelle structure) ne double rien.
	//
	// UNE RÈGLE NE VAUT QUE POUR UN CHAMP QUE L'ON VOIT. Un champ en faute que rien ne montre
	// grise « Enregistrer » sans rien de rouge à l'écran — l'utilisateur ne peut ni comprendre
	// ni corriger. Deux façons de ne pas voir un champ, deux réponses :
	//   • masqué par sa propre règle visible_when (le SIRET d'un particulier) : le validateur
	//     est CONDITIONNEL, il ne juge que si le champ est visible dans l'état du groupe, et le
	//     dépendant se revalide quand son pilote change (watchPilots) ;
	//   • hors de l'écran parce que son app-cuf n'est plus rendu (bloc « identité
	//     professionnelle » replié, bande d'adresse supprimée) : les règles partent avec le
	//     composant (clearRules, ngOnDestroy) — elles n'existent que tant que le champ est là.
	// Masquer n'efface toujours rien : la valeur reste et repart telle quelle.
	static applyRules(cufGroup: FormGroup | null | undefined, cufsStructure: common.Cuf[]): void {
		if (!cufGroup) return;
		for (const cuf of cufsStructure ?? []) {
			if (cuf.id === undefined || cuf.id === null) continue;
			const ctrl = cufGroup.get('' + cuf.id);
			if (!ctrl) continue;
			const d = cuf.data ?? {};
			const rules: ValidatorFn[] = [];
			if (d.mandatory) rules.push(Validators.required);
			if (d.typ === 'INT' || d.typ === 'FLOAT') {
				if (Number.isFinite(Number(d.min)) && d.min !== null && d.min !== '') rules.push(Validators.min(Number(d.min)));
				if (Number.isFinite(Number(d.max)) && d.max !== null && d.max !== '') rules.push(Validators.max(Number(d.max)));
			}
			if (d.typ === 'INT') rules.push(CufComponent.integer);
			if (typeof d.pattern === 'string' && d.pattern) rules.push(Validators.pattern(d.pattern));
			const composed = Validators.compose(rules);
			const conditional: ValidatorFn | null = (composed && d.visible_when)
				? (c: AbstractControl) => (CufComponent.isVisibleIn(cuf, c.parent as FormGroup | null) ? composed(c) : null)
				: composed;
			ctrl.setValidators(conditional);
			ctrl.updateValueAndValidity({ emitEvent: false });
		}
	}

	/** Le pendant d'applyRules : plus aucune règle sur ces champs — ils ne sont plus à l'écran. */
	static clearRules(cufGroup: FormGroup | null | undefined, cufsStructure: common.Cuf[]): void {
		if (!cufGroup) return;
		for (const cuf of cufsStructure ?? []) {
			if (cuf.id === undefined || cuf.id === null) continue;
			const ctrl = cufGroup.get('' + cuf.id);
			if (!ctrl || !ctrl.validator) continue;
			ctrl.clearValidators();
			ctrl.updateValueAndValidity({ emitEvent: false });
		}
	}

	// Entier ou vide. Le control porte une CHAÎNE pendant la saisie (« 175 ») et un nombre après
	// normalize() : les deux formes sont admises, « 1.75 » et « 1e3 » ne le sont pas.
	static integer(ctrl: AbstractControl): ValidationErrors | null {
		const v = ctrl.value;
		if (v === null || v === undefined || v === '') return null;
		return /^-?[0-9]+$/.test(String(v)) ? null : { integer: true };
	}

	unit(cuf: common.Cuf): string | null {
		const u = cuf.data?.unit;
		return (typeof u === 'string' && u.trim()) ? u.trim() : null;
	}

	/** L'infobulle du champ et de son étiquette : data.hint, sinon composée. */
	hint(cuf: common.Cuf): string {
		const d = cuf.data ?? {};
		if (typeof d.hint === 'string' && d.hint.trim()) return d.hint.trim();
		const parts: string[] = [cuf.name ?? ''];
		const unit = this.unit(cuf);
		const hasMin = d.min !== undefined && d.min !== null && d.min !== '';
		const hasMax = d.max !== undefined && d.max !== null && d.max !== '';
		const detail: string[] = [];
		if (unit) detail.push('en ' + unit);
		if (hasMin && hasMax) detail.push(`entre ${d.min} et ${d.max}`);
		else if (hasMin) detail.push(`au moins ${d.min}`);
		else if (hasMax) detail.push(`au plus ${d.max}`);
		if (d.typ === 'INT') detail.push('nombre entier');
		if (typeof d.pattern === 'string' && d.pattern) detail.push('format ' + d.pattern);
		if (d.mandatory) detail.push('obligatoire');
		if (detail.length) parts.push(detail.join(', '));
		return parts.filter(Boolean).join(' — ');
	}

	isInvalid(cuf: common.Cuf): boolean {
		return this.mode === 'edit' && !!this.cufGroup?.get('' + cuf.id)?.invalid;
	}

	/** Ce que le champ en faute dit dans son infobulle : la règle enfreinte, pas une généralité. */
	errorText(cuf: common.Cuf): string | null {
		const e = this.cufGroup?.get('' + cuf.id)?.errors;
		if (!e) return null;
		const unit = this.unit(cuf);
		const u = unit ? ' ' + unit : '';
		if (e['required']) return `${cuf.name} : obligatoire`;
		if (e['integer']) return `${cuf.name} : nombre entier attendu${u}`;
		if (e['min']) return `${cuf.name} : au moins ${e['min'].min}${u}`;
		if (e['max']) return `${cuf.name} : au plus ${e['max'].max}${u}`;
		if (e['pattern']) return (typeof cuf.data?.hint === 'string' && cuf.data.hint.trim()) ? cuf.data.hint.trim() : `${cuf.name} : format invalide`;
		return `${cuf.name} : valeur invalide`;
	}

	/** L'infobulle affichée : l'erreur si le champ est en faute, la consigne sinon. */
	title(cuf: common.Cuf): string {
		return this.errorText(cuf) ?? this.hint(cuf);
	}

	ngOnDestroy(): void {
		this.lovSubs.forEach((s) => s.unsubscribe());
		this.ruleSubs.forEach((s) => s.unsubscribe());
		// Les règles partent avec le composant : un champ qui n'est plus rendu ne peut plus
		// bloquer l'enregistrement (voir applyRules). Le groupe, lui, survit à l'écran. En
		// microtâche pour la même raison que la pose (ngOnChanges) : la destruction a lieu
		// pendant un cycle de détection.
		const group = this.ruledGroup;
		const structure = this.cufsStructure;
		this.ruledGroup = null;
		if (this.mode === 'edit') queueMicrotask(() => CufComponent.clearRules(group, structure));
	}

	// getValues$ est le flux CACHÉ du ValuesService : il ne complète jamais (il re-émet après
	// un updateData()), d'où le désabonnement ci-dessus. Un référentiel déjà demandé n'est pas
	// redemandé — plusieurs cufs peuvent viser le même.
	private loadLovs(): void {
		for (const cuf of this.cufsStructure ?? []) {
			const vid = cuf.data?.values_id;
			if (vid === undefined || vid === null) continue;
			const id = +vid;
			if (this.lovAll.has(id)) continue;
			this.lovAll.set(id, []);		// marque la demande : deux cufs sur le même groupe n'appellent qu'une fois
			this.lovSubs.push(this.valuesService.getValues$(id).subscribe((values: any) => {
				const actifs = (values ?? []).filter((v: any) => v?.is_active !== false);
				this.lovAll.set(id, actifs.map((v: any) => ({ label: v?.name ?? ('#' + v?.id), value: v?.id })));
				this.lovOpen.set(id, actifs.filter((v: any) => !v?.data?.closed_to_new)
					.map((v: any) => ({ label: v?.name ?? ('#' + v?.id), value: v?.id })));
			}));
		}
	}

	// ============================================================
	// LARGEUR D'UN CHAMP — data.width
	// ============================================================
	// En NOMBRE DE CARACTÈRES, pas en pixels : c'est ce que sait celui qui définit le champ
	// (« un SIRET fait 14 chiffres, 17 signes avec les espaces »), et c'est ce qui reste vrai
	// quand la police change. `ch` est la largeur du « 0 » de la police courante ; les 12 px
	// ajoutés sont le padding intérieur du champ, sans quoi le dernier caractère serait rogné.
	// Un style EN LIGNE, donc prioritaire sur les largeurs par type (.inputString 180 px…) et sur
	// celles que chaque écran impose à app-cuf — c'est le but : la définition du champ décide.
	// Sans data.width, rien n'est posé et les règles de l'écran s'appliquent comme avant.
	cufWidth(cuf: common.Cuf): string | null {
		const n = Number(cuf.data?.width);
		return Number.isFinite(n) && n > 0 ? `calc(${n}ch + 12px)` : null;
	}

	// ============================================================
	// LE BLOC « IDENTITÉ PROFESSIONNELLE » S'AFFICHE-T-IL ?
	// ============================================================
	// Écrite ICI et pas dans les écrans qui la posent : une règle métier recopiée trois fois
	// finit par diverger deux fois.
	//
	// UNE SEULE CHOSE REPLIE LE BLOC : la forme juridique choisie porte `is_private` dans le
	// référentiel des formes (aujourd'hui la seule « Personne physique »). D'où :
	//  - forme NON renseignée -> visible. L'absence de forme n'est pas une forme : replier sur un
	//    champ vide faisait agir l'écran comme si « Personne physique » était choisi.
	//  - forme « Personne physique » -> replié, MÊME si un champ du bloc porte encore une valeur.
	//    C'est le choix de l'utilisateur qui commande, pas le contenu des champs. Replier
	//    n'efface rien : les controls gardent leur valeur et repartent tels quels à
	//    l'enregistrement ; rechoisir une société les remontre intacts.
	//
	// LE DRAPEAU EST DANS LE RÉFÉRENTIEL, PAS ICI : ajouter demain une forme sans immatriculation
	// ne demande de toucher ni à ce fichier, ni aux champs — seulement de dire ce que vaut la
	// nouvelle forme. Le front ne connaît aucun id de forme juridique, il ne lit qu'un drapeau.
	static isLegalBlockVisible(cufGroup: FormGroup | null | undefined, formesJuridiques: any[]): boolean {
		if (!cufGroup) return false;
		const forme = cufGroup.get('' + CUF_PARTNER_LEGAL_FORM)?.value;
		if (forme === null || forme === undefined || forme === '') return true;
		const item = (formesJuridiques ?? []).find((f: any) => String(f?.id) === String(forme));
		return !item?.data?.is_private;
	}

	// ============================================================
	// AFFICHAGE CONDITIONNEL — data.visible_when
	// ============================================================
	// CE QUI N'EXISTAIT PAS AVANT : `id_component` sépare les cufs par ZONE d'écran (adresse,
	// tiers, conditions fournisseur, conditions commerciales…), jamais par NATURE de tiers.
	// Rien ne permettait de dire « ce champ ne concerne qu'un professionnel ».
	//
	// La règle vit dans la définition du cuf, pas dans le code d'un écran :
	//   "visible_when": {"cuf": -8, "not_in": [1]}   -> caché quand le cuf -8 vaut 1
	//   "visible_when": {"cuf": -8, "in": [20, 22]}  -> visible SEULEMENT pour ces valeurs
	// Elle vaut donc partout où app-cuf est rendu — fiche tiers, écrans à venir —
	// et se change en base sans toucher au front.
	//
	// LE CHAMP PILOTE VIDE LAISSE VOIR (`not_in`) : tant que la forme juridique n'est pas
	// renseignée, on ne sait pas si le tiers est un professionnel, et masquer sur une
	// supposition ferait disparaître des champs qu'il faut justement remplir. `in` fait
	// l'inverse : il ne montre que sur une valeur explicitement listée.
	//
	// MASQUER N'EFFACE PAS. Le control garde sa valeur et repart telle quelle à
	// l'enregistrement : passer une fiche en « Personne physique » cache le SIRET, il ne le
	// détruit pas. Une donnée ne se perd pas sur un changement d'affichage.
	isCufVisible(cuf: common.Cuf): boolean { return CufComponent.isVisibleIn(cuf, this.cufGroup); }

	/** La règle elle-même, sur un groupe donné : c'est aussi ce que le validateur conditionnel
	 *  d'applyRules consulte, sur le parent du control. */
	static isVisibleIn(cuf: common.Cuf, cufGroup: FormGroup | null | undefined): boolean {
		const regle = cuf.data?.visible_when;
		if (!regle || regle.cuf === undefined || regle.cuf === null) return true;
		const v = cufGroup?.get('' + regle.cuf)?.value;
		const vide = v === null || v === undefined || v === '';
		if (Array.isArray(regle.not_in)) return vide || !regle.not_in.some((x: any) => String(x) === String(v));
		if (Array.isArray(regle.in)) return !vide && regle.in.some((x: any) => String(x) === String(v));
		return true;
	}

	// Comparaison de la valeur du control avec celle des items : un entier du référentiel et la
	// même valeur héritée en chaîne doivent se reconnaître. null et undefined restent distincts
	// de « 0 » et de la chaîne vide — sans quoi un cuf vide sélectionnerait le premier élément
	// dont l'id vaut 0.
	lovCompare = (a: any, b: any): boolean => {
		if (a === null || a === undefined || b === null || b === undefined) return a === b;
		return String(a) === String(b);
	};

	getCufLovItems(cuf: common.Cuf): { label: string; value: any }[] {
		const vid = cuf.data?.values_id;
		if (vid === undefined || vid === null) return (cuf.data?.values ?? []).map((v: string) => ({ label: v, value: v }));
		const id = +vid;
		const ouverts = this.lovOpen.get(id) ?? [];
		const courant = this.cufGroup?.get('' + cuf.id)?.value;
		if (courant === null || courant === undefined || courant === '') return ouverts;
		return ouverts.some((i) => i.value === courant) ? ouverts : (this.lovAll.get(id) ?? ouverts);
	}

	// ============================================================
	// DATE — datepicker Material
	// ============================================================
	// Le control porte une DATE NUE « YYYY-MM-DD » : c'est ce qui est déjà stocké dans
	// c_partner.data.cufs (cuf 12 « Date de décès ») et c'est le contrat de time.ts pour un jour
	// calendaire. Le picker, lui, rend un objet moment ; on le repasse en chaîne dès la sélection
	// pour que le JSON posté ne parte jamais avec un instant UTC (décalage J-1 garanti sinon).
	// dateChange n'est émis que sur action utilisateur (calendrier ou change natif) : pas de boucle
	// avec le setValue qui suit.
	onCufDate(controlName: string, value: any): void {
		this.cufGroup.get(controlName)?.setValue(pickedDateStr(CufComponent.asDate(value)));
	}

	// ============================================================
	// FK — identifiant d'une autre ligne, saisi à la main — ET INT, depuis le 2026-09-15
	// ============================================================
	// Simple entier : AUCUN contrôle d'existence en base n'est fait ici (demande explicite).
	// Le même filtrage sert au type INT (une taille en centimètres, un niveau) : un
	// type="number" step="1" laissait taper « 1.75 » sans broncher.
	// Le champ est en type="text" et NON en type="number" : un input number laisse Firefox
	// AFFICHER des lettres (la valeur lue est vide, mais l'utilisateur voit son texte à l'écran) et
	// accepte « 1e3 » / « 1.5 » comme des nombres valides. Le filtrage est donc fait à la main —
	// touches au clavier, puis contenu (couvre le collage, le glisser-déposer et l'auto-remplissage).

	// Laisse passer les chiffres, un signe moins EN TÊTE (un niveau −1 est un sous-sol), les
	// touches d'édition/navigation et les raccourcis Ctrl/Cmd.
	blockNonInteger(event: KeyboardEvent): void {
		const edition = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
			'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
		if (event.ctrlKey || event.metaKey || edition.includes(event.key)) return;
		// `target` peut manquer (événement synthétique, test) : le signe est alors refusé.
		const input = event.target as HTMLInputElement | null;
		if (event.key === '-' && input?.selectionStart === 0 && !input.value.startsWith('-')) return;
		if (!/^[0-9]$/.test(event.key)) event.preventDefault();
	}

	// Filet de sécurité : tout ce qui n'est pas un chiffre (ou le signe de tête) est retiré du
	// champ, quelle que soit la façon dont c'est arrivé. L'input est remis à jour DANS LA
	// FOULÉE, sinon le texte refusé resterait affiché alors que le control, lui, ne le contient pas.
	sanitizeInt(controlName: string, event: Event): void {
		const input = event.target as HTMLInputElement;
		const raw = input.value ?? '';
		const clean = (raw.startsWith('-') ? '-' : '') + raw.replace(/[^0-9]/g, '');
		if (clean !== input.value) input.value = clean;
		this.cufGroup.get(controlName)?.setValue(clean === '' || clean === '-' ? null : clean);
	}

	onCufInt(controlName: string): void {
		const ctrl = this.cufGroup.get(controlName);
		ctrl?.setValue(CufComponent.asInt(ctrl.value));
	}

	deselRadioCuf(controlName: string, val: string, event: MouseEvent): void {
		const ctrl = this.cufGroup.get(controlName);
		if (ctrl?.value === val) {
			event.preventDefault();
			ctrl.setValue(null);
		}
	}

	cycleBoolCuf(controlName: string): void {
		const ctrl = this.cufGroup.get(controlName);
		const v = ctrl?.value;
		if (v === null || v === undefined || v === '') {
			ctrl?.setValue(true);
		} else if (v === true) {
			ctrl?.setValue(false);
		} else {
			ctrl?.setValue(null);
		}
	}

	// ============================================================
	// Normalisation avant POST
	// ============================================================
	// À appeler par l'écran hôte juste avant la sauvegarde. Deux raisons :
	//  - un input number rend une CHAÎNE ('70'), qui partirait telle quelle dans le JSONB et
	//    casserait toute comparaison numérique SQL ultérieure ;
	//  - le datepicker peut laisser un objet moment dans le control si la saisie n'a pas été
	//    quittée avant de valider le formulaire.
	// Seuls les controls SALIS sont retouchés : les cufs d'un autre composant présents dans le
	// même objet `cufs` (conditions fournisseur, encours…) ou une valeur héritée d'un ancien type
	// doivent repartir à l'identique, jamais être « corrigés » au passage.
	static normalize(cufGroup: FormGroup | null | undefined, cufsStructure: common.Cuf[]): void {
		if (!cufGroup) return;
		for (const cuf of cufsStructure ?? []) {
			if (cuf.id === undefined || cuf.id === null) continue;
			const ctrl = cufGroup.get('' + cuf.id);
			if (!ctrl || ctrl.pristine) continue;
			const v = ctrl.value;
			if (v === null || v === undefined || v === '') { ctrl.setValue(null, { emitEvent: false }); continue; }
			switch (cuf.data?.typ) {
				case 'DATE':  ctrl.setValue(pickedDateStr(CufComponent.asDate(v)), { emitEvent: false }); break;
				case 'INT':
				case 'FK':    ctrl.setValue(CufComponent.asInt(v), { emitEvent: false }); break;
				// LOV : entier SEULEMENT quand la liste vient d'un référentiel (values_id), car la
				// valeur est alors l'id de l'élément. Une LOV à liste en dur stocke la CHAÎNE
				// choisie — la caster la détruirait.
				case 'LOV':   if (cuf.data?.values_id !== undefined && cuf.data?.values_id !== null) ctrl.setValue(CufComponent.asInt(v), { emitEvent: false }); break;
				case 'FLOAT': { const n = parseFloat(v); ctrl.setValue(Number.isFinite(n) ? n : null, { emitEvent: false }); break; }
			}
		}
	}

	// moment (adaptateur du datepicker) ou dayjs → Date ; chaîne « YYYY-MM-DD » → telle quelle.
	private static asDate(v: any): Date | string | null {
		if (v === null || v === undefined || v === '') return null;
		return (typeof v?.toDate === 'function') ? v.toDate() : v;
	}

	private static asInt(v: any): number | null {
		const n = parseInt(v, 10);
		return Number.isFinite(n) ? n : null;
	}
}
